
import { useQuery } from '@tanstack/react-query';
import { getAttentionRequiredRentals, AttentionRequiredRental } from '@/utils/attention-required-api';

export type RefreshInterval = 0 | 30 | 60 | 300; // В секундах: 0 - выключено, 30с, 1м, 5м

interface UseAttentionRequiredRentalsOptions {
  days?: number;
  enabled?: boolean;
  refetchInterval?: RefreshInterval;
}

/**
 * Хук для получения списка арендаторов, требующих внимания,
 * с возможностью автоматического обновления данных
 */
export function useAttentionRequiredRentals({
  days = 5,
  enabled = true,
  refetchInterval = 0
}: UseAttentionRequiredRentalsOptions = {}) {
  
  const queryKey = ['attentionRequiredRentals', days];
  
  const {
    data = [],
    isLoading,
    isError,
    error,
    refetch,
    dataUpdatedAt
  } = useQuery({
    queryKey,
    queryFn: () => getAttentionRequiredRentals({ days }),
    enabled,
    refetchInterval: refetchInterval ? refetchInterval * 1000 : false, // Конвертируем секунды в миллисекунды
    refetchOnWindowFocus: true,
    staleTime: 60000, // 1 минута - после которой данные считаются устаревшими
    retry: 3, // Количество повторных попыток при ошибке
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Экспоненциальный откат
  });

  // Вычисляем время последнего обновления
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  return {
    rentals: data as AttentionRequiredRental[],
    isLoading,
    isError,
    error,
    refetch,
    lastUpdated
  };
}

/**
 * Получает строковое представление времени последнего обновления
 */
export function formatLastUpdated(lastUpdated: Date | null): string {
  if (!lastUpdated) return 'Нет данных';
  
  return lastUpdated.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}
